/**
 * EmailNotifications-ConfigFieldSchema — the typed-editor registry for email-rule custom content.
 *
 * The email handlers are SERVER-DRIVEN (GET Email/Handlers returns each handler's defaultConfiguration +
 * configurationDocs). Their config values are not all scalars: several are arrays or objects whose shape is
 * meaningful (a recipient list, `[field, value]` document filters, a role list, actor tags, a routing map).
 * Rendering those as a raw JSON textarea — what Home does, and what this app did before — makes them
 * effectively un-editable by an admin.
 *
 * This module maps a handler + field key onto a typed EDITOR id, and carries the pure parse/serialize
 * helpers each editor needs. It is deliberately DOM-free and pict-free so the round-tripping can be unit
 * tested (test/EmailNotificationsConfigFields_tests.js).
 *
 * Anything the registry does not recognize falls back to the inferred scalar type, and any unmodeled
 * array/object still lands on the 'json' escape hatch — so a handler that grows a new field shape keeps
 * working (degraded) instead of breaking.
 */

/**
 * Curated recipient tokens — the handlebars entities the notify handlers expose that resolve to an email
 * address. Handlers document more entities than these (Customer, Project, Document); only the ones that
 * carry an Email are offered as a one-click choice. Anything else is reachable via the Custom template kind.
 */
const RECIPIENT_TOKENS =
[
	{ Value: '{{CreatingUser.Email}}', Label: 'The person who created the document' },
	{ Value: '{{UpdatingUser.Email}}', Label: 'The person who last updated the document' },
];

/** The `ProjectRole:` prefix statusChangeNotification expands into every user holding that role on the project. */
const PROJECT_ROLE_PREFIX = 'ProjectRole:';

/**
 * Field key -> editor id. Keyed by field name because the handlers reuse the same names for the same shapes
 * (they share sharedConfigDocs). A handler-specific override can be added as `'<Handler>.<Key>'`.
 */
const FIELD_EDITORS =
{
	'Recipients': 'recipients',
	'FilterDocuments': 'documentfilters',
	'UsersWithRole': 'roles',
	'RequireAllActorTags': 'tags',
	'RequireAnyActorTags': 'tags',
	'SendTo': 'sendmap',
	'CC': 'emaillist',
	'BCC': 'emaillist',
	'SendRestTo': 'emaillist',
	'Subject': 'template',
	'BasedOn': 'template',
	'DocumentFilename': 'template',
	'TextMessage': 'body',
	'HTMLMessage': 'body',
};

/** Editors that manage their own value shape (so the generic scalar capture must skip them). */
const COMPLEX_EDITORS = [ 'recipients', 'documentfilters', 'roles', 'tags', 'sendmap', 'emaillist' ];

/**
 * Common document fields worth suggesting in the FilterDocuments field picker. Free text is still allowed —
 * FormData.Content.* paths are customer-specific and cannot be enumerated here.
 */
const DOCUMENT_FILTER_FIELDS =
[
	'Status',
	'IDProject',
	'IDDocumentType',
	'CreatingIDUser',
	'UpdatingIDUser',
	'FormData.Content.',
];

/**
 * Resolve the editor id for a config field.
 *
 * @param {String} pHandler - the handler name (e.g. 'StatusChangeNotification')
 * @param {String} pKey - the config field key
 * @param {*} pDefaultValue - the handler's default value for the key (the shape authority)
 * @param {*} pCurrentValue - the rule's stored value, used when the handler has no default for the key
 * @return {String} one of: recipients | documentfilters | roles | tags | sendmap | emaillist | template | body | checkbox | text | json
 */
function resolveFieldEditor(pHandler, pKey, pDefaultValue, pCurrentValue)
{
	const tmpScoped = FIELD_EDITORS[`${pHandler}.${pKey}`];
	if (tmpScoped) { return tmpScoped; }
	const tmpMapped = FIELD_EDITORS[pKey];
	if (tmpMapped) { return tmpMapped; }
	// Not in the registry — infer from the shape, preferring the handler default over the stored value.
	const tmpShape = (pDefaultValue === undefined) ? pCurrentValue : pDefaultValue;
	if (typeof tmpShape === 'boolean') { return 'checkbox'; }
	if (Array.isArray(tmpShape) || (tmpShape && typeof tmpShape === 'object')) { return 'json'; }
	const tmpString = (tmpShape == null) ? '' : String(tmpShape);
	if (tmpString.indexOf('\n') > -1) { return 'body'; }
	return 'text';
}

/** Whether an editor owns its own value shape (arrays/objects/chips) rather than a plain scalar input. */
function isComplexEditor(pEditor)
{
	return COMPLEX_EDITORS.indexOf(pEditor) > -1;
}

// --- Recipients ---------------------------------------------------------------------------------------

/**
 * Classify one stored recipient string into an editable row.
 *
 * @param {String} pValue
 * @return {{ Kind: String, Value: String }}  Kind: 'role' | 'token' | 'email' | 'template'
 */
function parseRecipient(pValue)
{
	const tmpValue = (pValue == null) ? '' : String(pValue);
	if (tmpValue.indexOf(PROJECT_ROLE_PREFIX) === 0)
	{
		return { Kind: 'role', Value: tmpValue.slice(PROJECT_ROLE_PREFIX.length) };
	}
	if (RECIPIENT_TOKENS.some((pToken) => pToken.Value === tmpValue))
	{
		return { Kind: 'token', Value: tmpValue };
	}
	if (tmpValue.indexOf('{{') > -1)
	{
		return { Kind: 'template', Value: tmpValue };
	}
	return { Kind: 'email', Value: tmpValue };
}

/** Turn an editable recipient row back into the string the handler expects. */
function serializeRecipient(pRow)
{
	if (!pRow) { return ''; }
	const tmpValue = (pRow.Value == null) ? '' : String(pRow.Value).trim();
	if (pRow.Kind === 'role')
	{
		return tmpValue ? `${PROJECT_ROLE_PREFIX}${tmpValue}` : '';
	}
	return tmpValue;
}

/** Parse a whole Recipients array into rows (dropping empties). */
function parseRecipients(pValue)
{
	return toArray(pValue).map(parseRecipient);
}

/** Serialize recipient rows, dropping rows the admin left blank. */
function serializeRecipients(pRows)
{
	return (Array.isArray(pRows) ? pRows : []).map(serializeRecipient).filter((pValue) => pValue !== '');
}

// --- Document filters ---------------------------------------------------------------------------------

/**
 * FilterDocuments is an array of `[field, value]` pairs. Parse defensively — a hand-edited config can hold
 * a malformed entry, and dropping it silently would change what the rule matches.
 *
 * @return {Array<{ Field: String, Value: String }>}
 */
function parseDocumentFilters(pValue)
{
	return toArray(pValue).map((pPair) =>
	{
		if (Array.isArray(pPair))
		{
			return { Field: String(pPair[0] == null ? '' : pPair[0]), Value: String(pPair[1] == null ? '' : pPair[1]) };
		}
		return { Field: String(pPair == null ? '' : pPair), Value: '' };
	});
}

/** Serialize filter rows back to `[field, value]` pairs, dropping rows with no field. */
function serializeDocumentFilters(pRows)
{
	return (Array.isArray(pRows) ? pRows : [])
		.filter((pRow) => pRow && String(pRow.Field || '').trim() !== '')
		.map((pRow) => [ String(pRow.Field).trim(), coerceFilterValue(pRow.Value) ]);
}

/**
 * Filter values are compared against raw column values, so a numeric-looking value must go over as a number
 * (`["UpdatingIDUser", 4]` in the handler docs) or the match silently fails.
 */
function coerceFilterValue(pValue)
{
	const tmpValue = (pValue == null) ? '' : String(pValue).trim();
	if (tmpValue !== '' && !isNaN(Number(tmpValue)) && /^-?\d+(\.\d+)?$/.test(tmpValue))
	{
		return Number(tmpValue);
	}
	return tmpValue;
}

// --- Roles --------------------------------------------------------------------------------------------

/**
 * UsersWithRole holds PermissionLevel strings OR numeric IDRoles. Keep both — an existing config may use
 * ids, and rewriting them to permission levels would change which users match.
 *
 * @return {Array<String|Number>}
 */
function parseRoleList(pValue)
{
	return toArray(pValue).filter((pEntry) => pEntry !== null && pEntry !== undefined && pEntry !== '');
}

// --- Tags / email chips -------------------------------------------------------------------------------

/** Actor-tag arrays: normalize to trimmed, de-duplicated strings. */
function parseTagList(pValue)
{
	return dedupe(toArray(pValue).map((pTag) => String(pTag == null ? '' : pTag).trim()).filter(Boolean));
}

/**
 * CC / BCC / SendRestTo are comma-separated email STRINGS (not arrays) — sharedConfig splits them on comma.
 * Parse to chips for editing.
 */
function parseEmailList(pValue)
{
	if (Array.isArray(pValue))
	{
		return dedupe(pValue.map((pEntry) => String(pEntry == null ? '' : pEntry).trim()).filter(Boolean));
	}
	return dedupe(String(pValue == null ? '' : pValue).split(',').map((pEntry) => pEntry.trim()).filter(Boolean));
}

/** Serialize email chips back to the comma-separated string the handlers split on. */
function serializeEmailList(pChips)
{
	return dedupe((Array.isArray(pChips) ? pChips : []).map((pEntry) => String(pEntry == null ? '' : pEntry).trim()).filter(Boolean)).join(',');
}

/** A permissive address check — good enough to warn on a typo without rejecting valid odd addresses. */
function looksLikeEmail(pValue)
{
	return /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/.test(String(pValue == null ? '' : pValue).trim());
}

// --- SendTo map ---------------------------------------------------------------------------------------

/**
 * ApprovalRollup's SendTo is `{ <BasedOn result>: "a@x.com,b@y.com" }`. Rows preserve key order.
 *
 * @return {Array<{ Key: String, Value: String }>}
 */
function parseSendMap(pValue)
{
	if (!pValue || typeof pValue !== 'object' || Array.isArray(pValue)) { return []; }
	return Object.keys(pValue).map((pKey) => ({ Key: pKey, Value: String(pValue[pKey] == null ? '' : pValue[pKey]) }));
}

/** Serialize SendTo rows, dropping rows with no key. A later row with a duplicate key wins (object semantics). */
function serializeSendMap(pRows)
{
	const tmpMap = {};
	(Array.isArray(pRows) ? pRows : []).forEach((pRow) =>
	{
		if (!pRow) { return; }
		const tmpKey = String(pRow.Key == null ? '' : pRow.Key).trim();
		if (!tmpKey) { return; }
		tmpMap[tmpKey] = serializeEmailList(parseEmailList(pRow.Value));
	});
	return tmpMap;
}

// --- Shared helpers -----------------------------------------------------------------------------------

/** Coerce a stored value to an array — a JSON-string column, a single value, or already an array. */
function toArray(pValue)
{
	if (Array.isArray(pValue)) { return pValue; }
	if (pValue === null || pValue === undefined || pValue === '') { return []; }
	if (typeof pValue === 'string')
	{
		try { const tmpParsed = JSON.parse(pValue); if (Array.isArray(tmpParsed)) { return tmpParsed; } }
		catch (pError) { /* not JSON — treat as a single entry below */ }
		return [ pValue ];
	}
	return [ pValue ];
}

function dedupe(pList)
{
	const tmpSeen = {};
	const tmpResult = [];
	(pList || []).forEach((pEntry) =>
	{
		const tmpKey = String(pEntry);
		if (!tmpSeen[tmpKey]) { tmpSeen[tmpKey] = true; tmpResult.push(pEntry); }
	});
	return tmpResult;
}

/** Deep-ish equality good enough for comparing a config value against its handler default (JSON-safe values). */
function valuesEqual(pLeft, pRight)
{
	if (pLeft === pRight) { return true; }
	try { return JSON.stringify(pLeft) === JSON.stringify(pRight); }
	catch (pError) { return false; }
}

/** A DOM-id-safe slug for a config key (handler keys are identifiers today, but stored blobs are freeform). */
function fieldSlug(pKey)
{
	return String(pKey == null ? '' : pKey).replace(/[^A-Za-z0-9_]/g, '_');
}

module.exports =
{
	RECIPIENT_TOKENS,
	PROJECT_ROLE_PREFIX,
	FIELD_EDITORS,
	DOCUMENT_FILTER_FIELDS,

	resolveFieldEditor,
	isComplexEditor,

	parseRecipient,
	serializeRecipient,
	parseRecipients,
	serializeRecipients,

	parseDocumentFilters,
	serializeDocumentFilters,
	coerceFilterValue,

	parseRoleList,
	parseTagList,

	parseEmailList,
	serializeEmailList,
	looksLikeEmail,

	parseSendMap,
	serializeSendMap,

	toArray,
	valuesEqual,
	fieldSlug,
};
